/**
 * Workflow Progress Banner Component
 *
 * Inline banner displayed on entity detail pages (LakehouseDetailPage,
 * WarehouseDetailPage) showing the current onboarding step and a link
 * to the next action. Provides contextual workflow guidance without
 * requiring navigation to the Workflow Dashboard.
 *
 * @module modules/workflows/components
 * @spec 135-ui-data-alignment
 * @story US2 - Workflow Guidance
 * @constitution v1.12.0 - Principle XXXVII (Code Header Standards)
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2, CircleDot } from 'lucide-react';
import type { EntityWorkflowStatus } from '../../../shared/types/workflow';
import { WorkflowStepIndicator } from './WorkflowStepIndicator';

// =============================================================================
// PROPS
// =============================================================================

export interface WorkflowProgressBannerProps {
  /** Workflow status for this entity (from useEntityWorkflowStatus hook) */
  status: EntityWorkflowStatus | null;
  /** Whether the data is still loading */
  isLoading?: boolean;
}

// =============================================================================
// COMPONENT
// =============================================================================

/**
 * WorkflowProgressBanner — Inline banner for entity detail pages.
 *
 * Shows the current onboarding progress with step indicators and a
 * prominent "Next Action" button. When fully complete, shows a
 * success banner.
 */
export const WorkflowProgressBanner: React.FC<WorkflowProgressBannerProps> = ({
  status,
  isLoading = false,
}) => {
  if (isLoading) {
    return (
      <div className="bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-lg p-4 animate-pulse">
        <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-48 mb-3" />
        <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-full" />
      </div>
    );
  }

  if (!status) {
    return null;
  }

  // Fully complete — success banner
  if (!status.nextAction) {
    return (
      <div
        className="flex items-center gap-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4"
        role="status"
        aria-label="Onboarding complete"
      >
        <CheckCircle2 className="w-5 h-5 text-green-600 dark:text-green-400 shrink-0" aria-hidden="true" />
        <div>
          <p className="text-sm font-medium text-green-800 dark:text-green-200">
            Onboarding Complete
          </p>
          <p className="text-xs text-green-600 dark:text-green-400">
            All {status.totalSteps} steps have been completed for this {status.entityType}.
          </p>
        </div>
      </div>
    );
  }

  // In progress — show steps + next action
  return (
    <div
      className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4"
      role="status"
      aria-label={`Onboarding progress: step ${status.currentStep} of ${status.totalSteps}`}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <CircleDot className="w-4 h-4 text-blue-600 dark:text-blue-400" aria-hidden="true" />
          <span className="text-sm font-medium text-blue-800 dark:text-blue-200">
            Onboarding Progress — Step {status.currentStep} of {status.totalSteps}
          </span>
        </div>
        <span className="text-xs text-blue-600 dark:text-blue-400 font-medium">
          {status.completionPct.toFixed(0)}%
        </span>
      </div>

      {/* Step Indicator */}
      <div className="mb-3">
        <WorkflowStepIndicator
          steps={status.steps}
          currentStep={status.currentStep}
          showLabels={false}
          size="sm"
        />
      </div>

      {/* Next Action */}
      <div className="flex items-center justify-between">
        <span className="text-xs text-blue-700 dark:text-blue-300">
          Next: {status.nextAction.stepName}
        </span>
        <Link
          to={status.nextAction.targetPath}
          className="inline-flex items-center gap-1 px-3 py-1 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 rounded transition-colors"
        >
          {status.nextAction.stepName}
          <ArrowRight className="w-3 h-3" aria-hidden="true" />
        </Link>
      </div>
    </div>
  );
};

export default WorkflowProgressBanner;
